import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SubscriptionFilter from "@/components/SubscriptionFilter";
import {
  ShieldCheck,
  ShieldAlert,
  Shield,
  Gauge,
  Clock,
  Database,
  Skull,
  DollarSign,
  Activity,
  AlertTriangle,
  ArrowRight,
  Search,
  Sparkles,
  RefreshCw,
  Loader2,
  Send,
  ChevronDown,
  Radar,
  Trash2,
  BookOpen,
  Server,
  Eye,
  GitCompareArrows,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const VALID_ROUTES = new Set(["/", "/costs", "/cleanup", "/resources", "/feed", "/changes", "/system", "/insights", "/hygiene", "/billing", "/admin", "/docs"]);

function isValidRoute(target) {
  if (!target) return false;
  const base = target.split("?")[0];
  return VALID_ROUTES.has(base);
}

const CAT = {
  security: { icon: Shield, accent: "var(--hig-danger)" },
  cost: { icon: DollarSign, accent: "var(--hig-success)" },
  hygiene: { icon: Skull, accent: "var(--hig-warning)" },
  change: { icon: Activity, accent: "var(--hig-accent)" },
  governance: { icon: BookOpen, accent: "#af52de" },
  answer: { icon: Sparkles, accent: "var(--hig-accent)" },
  system: { icon: Server, accent: "var(--n-text-mid)" },
};

const SEV = { critical: "var(--hig-danger)", high: "var(--hig-warning)", medium: "var(--hig-warning)", info: "var(--hig-accent)", low: "var(--hig-accent)" };

function getGreeting() {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}

/* ─── Insight Card ─── */
function InsightCard({ card, onAction, index }) {
  const cat = CAT[card.category] || CAT.answer;
  const Icon = cat.icon;
  const navigate = useNavigate();
  const dot = SEV[card.severity] || SEV.info;

  return (
    <div
      className={`group relative flex flex-col rounded-xl hig-card animate-card-reveal stagger-${Math.min(index + 1, 6)}`}
      style={{
        background: "var(--n-surface-2)",
        border: `1px solid color-mix(in srgb, ${cat.accent} 15%, transparent)`,
      }}
      data-testid={`insight-card-${card.id || index}`}
    >
      {/* Accent bar */}
      <div className="h-px w-full rounded-t-xl" style={{ background: `linear-gradient(90deg, color-mix(in srgb, ${cat.accent} 40%, transparent), color-mix(in srgb, ${cat.accent} 10%, transparent), transparent)` }} />

      <div className="p-6 flex-1 flex flex-col">
        {/* Header row: icon + severity */}
        <div className="flex items-center justify-between mb-4">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `color-mix(in srgb, ${cat.accent} 15%, transparent)` }}>
            <Icon className="w-4.5 h-4.5" style={{ color: cat.accent }} />
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: dot }} />
            <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: dot }}>{card.severity}</span>
          </div>
        </div>

        {/* Title */}
        <h3 className="text-[15px] font-semibold leading-snug mb-2.5 line-clamp-2">{card.title}</h3>

        {/* Description */}
        <p className="text-xs text-[var(--n-text-secondary)] leading-relaxed flex-1 line-clamp-3">{card.description}</p>

        {/* Action */}
        {card.action_label && (
          <button
            className="flex items-center gap-1.5 mt-4 text-xs font-semibold transition-all duration-200 group-hover:gap-2.5"
            style={{ color: cat.accent }}
            onClick={() => {
              if (card.action_type === "navigate") {
                navigate(isValidRoute(card.action_target) ? card.action_target : "/");
              } else if (card.action_type === "command") {
                onAction?.(card.action_target);
              }
            }}
            data-testid={`insight-action-${card.id || index}`}
          >
            {card.action_label} <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
          </button>
        )}
      </div>
    </div>
  );
}

/* ─── Stat Block ─── */
function StatBlock({ icon: Icon, label, value, accent }) {
  return (
    <div className="flex items-center gap-3" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `color-mix(in srgb, ${accent} 15%, transparent)` }}>
        <Icon className="w-4 h-4" style={{ color: accent }} />
      </div>
      <div className="min-w-0">
        <div className="text-base font-bold font-mono leading-none" style={{ color: accent }}>{value}</div>
        <div className="text-[10px] text-[var(--n-text-muted)] mt-1 uppercase tracking-wider">{label}</div>
      </div>
    </div>
  );
}

/* ─── Page ─── */
export default function InsightsHomePage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [status, setStatus] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [chips, setChips] = useState([]);
  const [statusLoading, setStatusLoading] = useState(true);
  const [briefingLoading, setBriefingLoading] = useState(true);
  const [command, setCommand] = useState("");
  const [commanding, setCommanding] = useState(false);
  const [commandCards, setCommandCards] = useState(null);
  const [showMore, setShowMore] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState("");
  const [scanning, setScanning] = useState(false);

  const [refreshing, setRefreshing] = useState(false);

  const fetchAll = useCallback(async () => {
    setStatusLoading(true);
    setBriefingLoading(true);
    const params = new URLSearchParams();
    if (subscriptionId) params.append("subscription_id", subscriptionId);

    const [statusRes, briefingRes, chipsRes] = await Promise.allSettled([
      api().get(`/insights/status?${params}`),
      api().get(`/insights/briefing?${params}`),
      api().get("/insights/chips"),
    ]);

    if (statusRes.status === "fulfilled") setStatus(statusRes.value.data);
    setStatusLoading(false);
    if (chipsRes.status === "fulfilled") setChips(chipsRes.value.data);
    if (briefingRes.status === "fulfilled") setBriefing(briefingRes.value.data);
    setBriefingLoading(false);
  }, [api, subscriptionId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const refreshBriefing = async () => {
    setRefreshing(true);
    setCommandCards(null);
    try {
      const params = new URLSearchParams();
      if (subscriptionId) params.append("subscription_id", subscriptionId);
      const res = await api().post(`/insights/refresh?${params}`);
      setBriefing(res.data);
      toast.success("Fresh insights generated");
    } catch { toast.error("Refresh failed"); }
    finally { setRefreshing(false); }
  };

  const runCommand = async (q) => {
    const query = q || command;
    if (!query.trim()) return;
    setCommanding(true);
    setCommandCards(null);
    try {
      const res = await api().post("/insights/command", { query, subscription_id: subscriptionId || null });
      setCommandCards(res.data.cards || []);
      if (res.data.subscription_suggestion) setSubscriptionId(res.data.subscription_suggestion);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Command failed");
    } finally {
      setCommanding(false);
      setCommand("");
    }
  };

  const triggerScan = async () => {
    setScanning(true);
    try {
      await api().post("/scan/trigger");
      toast.success("Scan complete");
      fetchAll();
    } catch { toast.error("Scan failed"); }
    finally { setScanning(false); }
  };

  const handleKeyDown = (e) => { if (e.key === "Enter") { e.preventDefault(); runCommand(); } };

  const secIcon = status?.security_status === "critical" ? ShieldAlert : status?.security_status === "warning" ? Shield : ShieldCheck;
  const secColor = status?.security_status === "critical" ? "var(--hig-danger)" : status?.security_status === "warning" ? "var(--hig-warning)" : "var(--hig-accent)";
  const secLabel = status?.security_status === "critical" ? "Critical" : status?.security_status === "warning" ? "Warning" : "Secure";

  const visibleCards = commandCards || briefing?.cards || [];
  const shownCards = showMore ? visibleCards : visibleCards.slice(0, 3);
  const hasMore = visibleCards.length > 3;
  const scanAgo = status?.last_scan ? `${Math.max(1, Math.round((Date.now() - new Date(status.last_scan).getTime()) / 60000))}m` : "—";
  const displayName = user?.name?.split(" ")[0] || "there";

  return (
    <div className="space-y-8 max-w-[1100px] mx-auto" data-testid="insights-home-page">

      {/* ── Greeting + Filter ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight" data-testid="greeting">{getGreeting()}, {displayName}</h1>
          <p className="text-[11px] text-[var(--n-text-muted)] mt-0.5">{new Date().toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <SubscriptionFilter value={subscriptionId} onChange={(v) => { setSubscriptionId(v); setCommandCards(null); }} />
          <Button onClick={triggerScan} disabled={scanning} variant="ghost" size="icon" className="h-8 w-8 text-[var(--n-text-muted)] hover:text-[var(--hig-accent)]" data-testid="scan-now-btn">
            {scanning ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Radar className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      {/* ── Status Strip ── */}
      <div className="flex flex-wrap gap-x-8 gap-y-4 px-6 py-5 rounded-xl bg-[var(--n-surface)] border border-[var(--n-border)]" data-testid="status-strip">
        {statusLoading ? (
          [...Array(6)].map((_, i) => <div key={i} className="h-9 w-24 rounded bg-[var(--n-skeleton)] animate-pulse" />)
        ) : status && (
          <>
            <StatBlock icon={secIcon} label="Security" value={secLabel} accent={secColor} />
            <StatBlock icon={Gauge} label="Efficiency" value={`${status.efficiency_score}%`} accent={status.efficiency_score >= 80 ? "var(--hig-accent)" : status.efficiency_score >= 60 ? "var(--hig-warning)" : "var(--hig-danger)"} />
            <StatBlock icon={Database} label="Resources" value={status.resource_count.toLocaleString()} accent="var(--hig-accent)" />
            <StatBlock icon={Skull} label="Orphans" value={status.orphan_count} accent={status.orphan_count > 0 ? "var(--hig-warning)" : "var(--hig-accent)"} />
            <StatBlock icon={DollarSign} label="Spend" value={`$${Math.round(status.total_cost / 1000)}k`} accent="var(--hig-success)" />
            <StatBlock icon={Clock} label="Last Scan" value={scanAgo} accent="var(--n-text-muted)" />
          </>
        )}
      </div>

      {/* ── Command Bar ── */}
      <div data-testid="command-bar-container">
        <div className="relative group">
          <div className="absolute -inset-px rounded-xl bg-gradient-to-r from-[var(--hig-accent)]/0 via-[var(--hig-accent)]/15 to-[var(--hig-accent)]/0 opacity-0 group-focus-within:opacity-100 transition-opacity duration-700" />
          <div className="relative flex items-center bg-[var(--n-surface)] border border-[var(--n-border-2)] rounded-xl group-focus-within:border-[var(--hig-accent)]/20 transition-all duration-300">
            <Search className="w-4 h-4 text-[var(--n-text-dim)] ml-4 shrink-0" />
            <Input
              ref={inputRef}
              value={command}
              onChange={(e) => setCommand(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask anything... e.g. 'which subscription needs attention?'"
              className="border-0 bg-transparent h-11 text-sm focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-[var(--n-text-dim)]"
              disabled={commanding}
              data-testid="command-input"
            />
            <Button
              onClick={() => runCommand()}
              disabled={commanding || !command.trim()}
              variant="ghost" size="icon"
              className="mr-2 h-8 w-8 text-[var(--hig-accent)] hover:bg-[var(--hig-accent)]/10 disabled:opacity-20"
              data-testid="command-submit"
            >
              {commanding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        {chips.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid="prompt-chips">
            {chips.map((chip, i) => (
              <button
                key={i}
                onClick={() => { setCommand(chip.command); runCommand(chip.command); }}
                disabled={commanding}
                className="px-3 py-1.5 rounded-lg text-xs font-medium text-[var(--n-text-muted)] bg-transparent border border-[var(--n-border-2)] hover:text-[var(--hig-accent)] hover:border-[var(--hig-accent)]/20 transition-all duration-200 disabled:opacity-30"
                data-testid={`chip-${i}`}
              >
                {chip.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Section Label ── */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2.5">
          <Sparkles className="w-4 h-4 text-[var(--hig-accent)]" />
          <span className="text-xs font-semibold text-[var(--n-text-secondary)] uppercase tracking-widest">
            {commandCards ? "AI Response" : "Today's Briefing"}
          </span>
        </div>
        <div className="flex-1 h-px bg-[var(--n-border)]" />
        {commandCards ? (
          <button
            onClick={() => setCommandCards(null)}
            className="flex items-center gap-1 text-[10px] text-[var(--n-text-muted)] hover:text-foreground transition-colors"
            data-testid="clear-command-results"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        ) : (
          <button
            onClick={refreshBriefing}
            disabled={refreshing}
            className="group flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-[var(--n-text-muted)] hover:text-[var(--hig-accent)] hover:bg-[var(--hig-accent-subtle)] transition-all disabled:opacity-40"
            data-testid="refresh-briefing-btn"
            title="Get fresh insights"
          >
            <RefreshCw className={`w-3.5 h-3.5 transition-transform duration-500 group-hover:rotate-45 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
        )}
      </div>

      {/* ── Insight Cards ── */}
      {briefingLoading || commanding ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-[200px] rounded-xl bg-[var(--n-surface-2)] border border-[var(--n-border)] animate-pulse" style={{ animationDelay: `${i * 100}ms` }} />
          ))}
        </div>
      ) : shownCards.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="insight-cards-grid">
          {shownCards.map((card, i) => (
            <InsightCard key={card.id || i} card={card} index={i} onAction={(cmd) => { setCommand(cmd); runCommand(cmd); }} />
          ))}
        </div>
      ) : null}

      {hasMore && !briefingLoading && !commanding && (
        <button
          onClick={() => setShowMore(!showMore)}
          className="flex items-center gap-1 mx-auto text-xs text-[var(--n-text-muted)] hover:text-[var(--n-text-secondary)] transition-colors"
          data-testid="show-more-insights"
        >
          <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${showMore ? "rotate-180" : ""}`} />
          {showMore ? "Show less" : `${visibleCards.length - 3} more`}
        </button>
      )}

      {/* ── Meta Insight ── */}
      {briefing?.meta_insight && !commandCards && !briefingLoading && (
        <div className="flex items-start gap-3 px-5 py-4 rounded-xl bg-[var(--n-surface)] border border-[var(--n-border)]" data-testid="meta-insight">
          <Eye className="w-3.5 h-3.5 text-[var(--hig-accent)]/40 shrink-0 mt-0.5" />
          <p className="text-xs text-[var(--n-text-muted)] leading-relaxed">{briefing.meta_insight}</p>
        </div>
      )}

      {/* ── Quick Nav ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="quick-nav">
        {[
          { label: "Cost Intelligence", path: "/costs", icon: DollarSign, accent: "var(--hig-success)" },
          { label: "Cleanup Wizard", path: "/cleanup", icon: Trash2, accent: "var(--hig-warning)" },
          { label: "Resources", path: "/resources", icon: Database, accent: "var(--hig-accent)" },
          { label: "Changes", path: "/changes", icon: GitCompareArrows, accent: "#af52de" },
        ].map((item) => (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            className="flex items-center gap-2.5 px-4 py-3.5 rounded-xl bg-[var(--n-surface)] border border-[var(--n-border)] hig-card text-left group"
            data-testid={`quick-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
          >
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `color-mix(in srgb, ${item.accent} 15%, transparent)` }}>
              <item.icon className="w-3.5 h-3.5" style={{ color: item.accent }} />
            </div>
            <span className="text-xs font-medium text-[var(--n-text-muted)] group-hover:text-[var(--n-text-secondary)] transition-colors">{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
